import React, { useState, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";
import { toast } from "sonner";
import Reveal from "@/components/shared/Reveal";
import Seo from "@/components/shared/Seo";
import Btn from "@/components/shared/Btn";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PROFILES } from "@/lib/constants";
import { CheckCircle2, AlertTriangle, UserPlus } from "lucide-react";

const empty = {
  first_name: "", last_name: "", email: "", phone: "", company: "", function: "",
  profile_type: "", profile_other: "", city: "", gdpr_consent: false,
};
const emptyComp = { first_name: "", last_name: "", email: "", company: "", function: "", profile_type: "", profile_other: "" };

const errMessage = (err, fallback = "Une erreur est survenue.") => {
  const d = err?.response?.data?.detail;
  if (typeof d === "string") return d;
  if (d && typeof d === "object" && !Array.isArray(d) && d.message) return d.message;
  return fallback;
};

const cleanCompanion = (c) => {
  const out = {};
  Object.entries(c).forEach(([k, v]) => { if (v !== "" && v != null) out[k] = v; });
  return out;
};

const Inscription = () => {
  const { data: openEditions = [] } = useQuery({ queryKey: ["editions-open"], queryFn: async () => (await api.get("/editions/open")).data });
  const { data: allEditions = [] } = useQuery({ queryKey: ["editions"], queryFn: async () => (await api.get("/editions")).data });
  const [editionId, setEditionId] = useState("");
  const ed = useMemo(() => openEditions.find((e) => e.id === editionId) || openEditions[0], [openEditions, editionId]);
  const closedMsg = useMemo(() => {
    const cand = allEditions.find((e) => e.is_active) || allEditions.find((e) => e.status === "active");
    return cand?.registration_closed_message || "Les inscriptions ne sont pas ouvertes actuellement.";
  }, [allEditions]);
  const [form, setForm] = useState(empty);
  const [withComp, setWithComp] = useState(false);
  const [comp, setComp] = useState(emptyComp);
  const [sending, setSending] = useState(false);
  const [screen, setScreen] = useState("form"); // form | done | duplicate
  const [recoverEmail, setRecoverEmail] = useState("");
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e?.target ? e.target.value : e }));
  const setC = (k) => (e) => setComp((c) => ({ ...c, [k]: e?.target ? e.target.value : e }));
  const activeEd = ed;

  const submit = async (e) => {
    e.preventDefault();
    if (!form.gdpr_consent) return toast.error("Merci d'accepter le traitement de vos données (RGPD).");
    if (!activeEd) return toast.error("Aucune édition ouverte à l'inscription pour le moment.");
    setSending(true);
    try {
      await api.post("/registrations", {
        ...form, edition_id: activeEd.id, with_companion: withComp,
        companion: withComp ? cleanCompanion(comp) : null,
      });
      setScreen("done");
    } catch (err) {
      if (err?.response?.status === 409) setScreen("duplicate");
      else toast.error(errMessage(err));
    }
    setSending(false);
  };

  const resend = async (emailArg) => {
    const email = emailArg || form.email || recoverEmail;
    if (!email) return toast.error("Indiquez votre e-mail.");
    try {
      const res = await api.post("/registrations/resend", { email, edition_id: activeEd?.id });
      toast.success(res.data?.message || "Message renvoyé.");
    } catch { toast.error("Impossible de renvoyer pour l'instant."); }
  };

  const RecoverBox = () => (
    <div className="bg-white border border-f2b-filet p-6 mt-6" data-testid="recover-box">
      <p className="f2b-kicker text-f2b-gray mb-2">Déjà inscrit·e ?</p>
      <p className="text-sm text-f2b-gray mb-3">Récupérez votre billet ou renvoyez votre confirmation (disponible même quand les inscriptions sont fermées).</p>
      <div className="flex flex-col sm:flex-row gap-2">
        <input type="email" value={recoverEmail} onChange={(e) => setRecoverEmail(e.target.value)} placeholder="Votre e-mail"
          className="flex-1 border border-f2b-filet px-3 py-2 text-sm" data-testid="recover-email" />
        <button type="button" onClick={() => resend(recoverEmail)} className="bg-f2b-black text-white px-4 py-2 text-sm" data-testid="recover-submit">Récupérer mon billet</button>
      </div>
    </div>
  );

  return (
    <>
      <Seo title="S'inscrire — FACADES2build" description="Demandez à participer à FACADES2build. Inscription nominative soumise à validation." />
      <section className="bg-f2b-black pt-40 pb-16">
        <div className="f2b-container max-w-3xl">
          <Reveal><p className="f2b-kicker text-f2b-cyan mb-4">Participer · Événement gratuit sur validation</p></Reveal>
          <Reveal delay={0.08}><h1 className="font-display font-bold text-white text-4xl md:text-5xl leading-tight">Demander à participer</h1></Reveal>
          <Reveal delay={0.16}>
            <p className="text-white/70 mt-5">
              L'accès est gratuit mais nominatif. Votre demande est enregistrée puis <strong className="text-white">validée par notre équipe</strong> ;
              vous recevrez ensuite votre billet personnel avec QR code.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="py-16 bg-f2b-light">
        <div className="f2b-container max-w-2xl">
          {screen === "done" && (
            <Reveal className="bg-white border border-f2b-filet p-10 text-center" data-testid="inscription-done">
              <CheckCircle2 size={44} className="text-f2b-cyan mx-auto mb-4" />
              <h2 className="font-display font-bold text-f2b-black text-2xl">Demande bien reçue</h2>
              <p className="text-f2b-gray mt-3">Votre demande d'inscription est enregistrée (statut <strong>À valider</strong>). Un e-mail de confirmation de réception vient de vous être envoyé. Votre participation sera confirmée séparément, avec votre billet.</p>
            </Reveal>
          )}

          {screen === "duplicate" && (
            <Reveal className="bg-white border border-f2b-gold p-10 text-center" data-testid="inscription-duplicate">
              <AlertTriangle size={44} className="text-f2b-gold mx-auto mb-4" />
              <h2 className="font-display font-bold text-f2b-black text-2xl">Vous êtes déjà enregistré(e)</h2>
              <p className="text-f2b-gray mt-3">Vous êtes déjà inscrit(e) ou votre demande est déjà enregistrée pour cet événement.</p>
              <div className="flex flex-wrap gap-3 justify-center mt-6">
                <Btn variant="cyan" onClick={resend} data-testid="resend-confirmation">Renvoyer ma confirmation</Btn>
                <Btn variant="outlineDark" onClick={resend} data-testid="recover-ticket">Récupérer mon billet</Btn>
              </div>
            </Reveal>
          )}

          {screen === "form" && (
            <Reveal>
              {openEditions.length === 0 ? (
                <div>
                  <div className="bg-white border border-f2b-gold p-10 text-center" data-testid="no-open-edition">
                    <h2 className="font-display font-bold text-f2b-black text-2xl mb-3">Inscriptions fermées</h2>
                    <p className="text-f2b-gray">{closedMsg}</p>
                    <a href="/edition-2028" className="inline-block mt-5 text-f2b-cyan f2b-link-underline">Être informé de l'édition 2028 →</a>
                  </div>
                  <RecoverBox />
                </div>
              ) : (
                <form onSubmit={submit} className="bg-white p-8 border border-f2b-filet space-y-4" data-testid="inscription-form">
                  {openEditions.length > 1 && (
                    <div>
                      <label className="block text-sm font-medium text-f2b-text mb-1.5">Édition</label>
                      <Select value={activeEd?.id} onValueChange={setEditionId}>
                        <SelectTrigger data-testid="insc-edition"><SelectValue /></SelectTrigger>
                        <SelectContent>{openEditions.map((e) => <SelectItem key={e.id} value={e.id}>{e.date_label} — {e.city}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                  )}
                  {activeEd && (
                    <div className="bg-f2b-light p-4 text-sm text-f2b-gray">
                      <strong className="text-f2b-black">{activeEd.theme}</strong><br />
                      {activeEd.date_label} · {activeEd.schedule} · {activeEd.venue || activeEd.city}
                    </div>
                  )}

                  <p className="f2b-kicker text-f2b-gray pt-2">Titulaire</p>
                  <div className="grid grid-cols-2 gap-4">
                    <Input required placeholder="Prénom *" value={form.first_name} onChange={set("first_name")} data-testid="insc-firstname" />
                    <Input required placeholder="Nom *" value={form.last_name} onChange={set("last_name")} data-testid="insc-lastname" />
                  </div>
                  <Input required type="email" placeholder="E-mail professionnel *" value={form.email} onChange={set("email")} data-testid="insc-email" />
                  <div className="grid grid-cols-2 gap-4">
                    <Input placeholder="Téléphone" value={form.phone} onChange={set("phone")} data-testid="insc-phone" />
                    <Input placeholder="Ville" value={form.city} onChange={set("city")} data-testid="insc-city" />
                  </div>
                  <Input required placeholder="Société / agence *" value={form.company} onChange={set("company")} data-testid="insc-company" />
                  <Input placeholder="Fonction" value={form.function} onChange={set("function")} data-testid="insc-function" />
                  <Select value={form.profile_type} onValueChange={(v) => setForm((f) => ({ ...f, profile_type: v }))}>
                    <SelectTrigger data-testid="insc-profile"><SelectValue placeholder="Profil professionnel *" /></SelectTrigger>
                    <SelectContent>{PROFILES.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
                  </Select>
                  {form.profile_type === "Autre" && (
                    <Input placeholder="Précisez" value={form.profile_other} onChange={set("profile_other")} data-testid="insc-profile-other" />
                  )}

                  <div className="flex items-center justify-between border border-f2b-filet p-4">
                    <span className="flex items-center gap-2 text-sm text-f2b-text"><UserPlus size={18} className="text-f2b-cyan" /> Je souhaite être accompagné(e)</span>
                    <Switch checked={withComp} onCheckedChange={setWithComp} data-testid="insc-with-companion" />
                  </div>

                  {withComp && (
                    <div className="border border-f2b-filet p-4 space-y-4 bg-f2b-light/50" data-testid="companion-fields">
                      <p className="f2b-kicker text-f2b-gray">Accompagnant(e)</p>
                      <div className="grid grid-cols-2 gap-4">
                        <Input required placeholder="Prénom *" value={comp.first_name} onChange={setC("first_name")} data-testid="comp-firstname" />
                        <Input required placeholder="Nom *" value={comp.last_name} onChange={setC("last_name")} data-testid="comp-lastname" />
                      </div>
                      <Input type="email" placeholder="E-mail" value={comp.email} onChange={setC("email")} data-testid="comp-email" />
                      <Input placeholder="Société (si différente)" value={comp.company} onChange={setC("company")} data-testid="comp-company" />
                      <Select value={comp.profile_type} onValueChange={(v) => setComp((c) => ({ ...c, profile_type: v }))}>
                        <SelectTrigger data-testid="comp-profile"><SelectValue placeholder="Profil (facultatif)" /></SelectTrigger>
                        <SelectContent>{PROFILES.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                  )}

                  <label className="flex items-start gap-3 text-sm text-f2b-gray">
                    <Checkbox checked={form.gdpr_consent} onCheckedChange={(v) => setForm((f) => ({ ...f, gdpr_consent: !!v }))} data-testid="insc-gdpr" />
                    <span>J'accepte que mes données soient traitées pour la gestion de mon inscription à FACADES2build (RGPD). Elles ne sont ni publiées ni cédées.</span>
                  </label>
                  <Btn type="submit" variant="cyan" className="w-full" disabled={sending} data-testid="insc-submit">{sending ? "Envoi…" : "Envoyer ma demande"}</Btn>
                </form>
              )}
              {openEditions.length > 0 && <RecoverBox />}
            </Reveal>
          )}
        </div>
      </section>
    </>
  );
};

export default Inscription;
